import { useCallback } from "react";

import { useBreakpointV2 } from "@tm/ui-widgets";

import FormattedText, {
  getPlainText,
  type TextNodeType,
} from "../../../../components/FormattedText/FormattedText";
import { useClaimAction } from "../../modalManager";
import { ModalActionId } from "../../modalManager/types";
import type { ClaimRequestCardData } from "../../types";
import {
  BottomRow,
  CriteriaDesc,
  CriteriaName,
  DesktopEligibilityCard,
  DesktopPaxWrap,
  EligibilityCta,
  EligibilityTitle,
  PaxIconWrap,
  PaxRow,
  RequestHeader,
  RequestIcon,
  RequestSubText,
  RequestTitle,
  SummaryCard,
} from "./ClaimRequestSummary.style";

export interface ClaimRequestSummaryProps {
  data?: ClaimRequestCardData;
}

export function ClaimRequestSummary({ data }: ClaimRequestSummaryProps) {
  const device = useBreakpointV2();
  const isMobile = device === "sm";
  const runClaimAction = useClaimAction();

  const eligibility = data?.checkEligibilityCta;
  const eligibilityData = eligibility?.action?.data;
  const criteria = eligibilityData?.criteria ?? [];
  const eligibilityCta = eligibility?.text;
  const eligibilityTitle = eligibilityData?.title ?? eligibilityCta;

  const openEligibility = useCallback(() => {
    if (!eligibilityData?.criteria?.length) return;
    runClaimAction(ModalActionId.CHECK_ELIGIBILITY, {
      extra: { eligibilityData },
    });
  }, [runClaimAction, eligibilityData]);

  if (!data) return null;

  const hasTitle = Boolean(
    getPlainText(data.title as TextNodeType | undefined).trim()
  );
  const hasPax = Boolean(getPlainText(data.paxInfo?.text).trim());

  const paxBlock = hasPax ? (
    <PaxRow>
      {data.paxInfo?.iconUrl ? (
        <PaxIconWrap>
          <img src={data.paxInfo.iconUrl} alt="" />
        </PaxIconWrap>
      ) : null}
      <FormattedText value={data.paxInfo?.text} />
    </PaxRow>
  ) : null;

  return (
    <>
      <SummaryCard>
        <RequestHeader>
          {hasTitle ? (
            <RequestTitle>
              <FormattedText value={data.title as TextNodeType} />
            </RequestTitle>
          ) : null}
          {data.iconUrl ? <RequestIcon src={data.iconUrl} alt="" /> : null}
        </RequestHeader>
        {getPlainText(data.subText).trim() ? (
          <RequestSubText>
            <FormattedText value={data.subText} />
          </RequestSubText>
        ) : null}

        {isMobile ? (
          <BottomRow>
            {paxBlock}
            {criteria.length > 0 ? (
              <EligibilityCta type="button" onClick={openEligibility}>
                <FormattedText value={eligibilityCta} />
              </EligibilityCta>
            ) : null}
          </BottomRow>
        ) : paxBlock ? (
          <DesktopPaxWrap>{paxBlock}</DesktopPaxWrap>
        ) : null}
      </SummaryCard>

      {!isMobile && criteria.length > 0 ? (
        <DesktopEligibilityCard>
          {getPlainText(eligibilityTitle).trim() ? (
            <EligibilityTitle>
              <FormattedText value={eligibilityTitle} />
            </EligibilityTitle>
          ) : null}
          {criteria.map((item, idx) => (
            <div key={item.id ?? idx}>
              {getPlainText(item.name).trim() ? (
                <CriteriaName>
                  <FormattedText value={item.name} />
                </CriteriaName>
              ) : null}
              {getPlainText(item.description).trim() ? (
                <CriteriaDesc>
                  <FormattedText value={item.description} />
                </CriteriaDesc>
              ) : null}
            </div>
          ))}
        </DesktopEligibilityCard>
      ) : null}
    </>
  );
}
